import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { FC, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRedirectOnAuth } from '../../aws/AuthHooks';
import { AuthContext, AuthState } from '../../aws/AuthProvider';
import { DataContext } from '../../aws/DataProvider';
import { Templates as TemplatesTypes, Utils } from '@hellohair/types';
import FormInput from '../../components/form-components/form-input/FormInput';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { createTemplate, updateTemplate } from '../../aws/TemplatesApi';
import { ConfirmDialog } from '../../components/modal/ConfirmDialog';
import { deepEqual } from '../../helpers/deepEqual';
import SurveySingle from '../legacy/Survey/Single/SurveySingle';
import { useStyles } from '../../theme/styles.helpers';
import { ROUTE } from '../routes';
import { TemplateStyles } from './Template.Styles';
import { useNavigate } from 'react-router-dom';
import { SurveyLive } from '@hellohair/survey-form/dist/index';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Template: FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(TemplateStyles, {});

  const {
    formDetailsState: [formDetails],
  } = useContext(DataContext);
  const { apiClient } = useContext(AuthContext);

  useRedirectOnAuth([AuthState.UNAUTHORIZED], ROUTE.LOGIN);

  const { handleSubmit, control, formState } = useForm<Utils.DBItem<TemplatesTypes.SurveyTemplate>>({
    defaultValues: formDetails ?? {},
  });

  const navigate = useNavigate();

  /**
   * Baseline for the dirty check, captured the first render the template is
   * there. The step editors write straight into `formDetails`, so react-hook-form
   * only knows about the name - the steps have to be compared by hand.
   */
  const initialSteps = useRef<TemplatesTypes.StepData[] | null>(null);
  if (initialSteps.current === null && formDetails) initialSteps.current = formDetails.steps ?? [];

  const dirty =
    formState.isDirty || (!!initialSteps.current && !deepEqual(formDetails?.steps ?? [], initialSteps.current));

  const [confirmLeave, setConfirmLeave] = useState(false);
  const leave = () => navigate(ROUTE.FORMS);
  /** Cancel discards every unsaved step edit too, so it asks first. */
  const requestLeave = () => {
    if (dirty) setConfirmLeave(true);
    else leave();
  };

  // onSubmit
  const onSubmit: SubmitHandler<Utils.DBItem<TemplatesTypes.SurveyTemplate>> = async (data) => {
    if (!formDetails) {
      const updateLoad: TemplatesTypes.TemplateAddInput = {
        name: data?.name || 'name',
        description: data?.description || 'description',
      };

      console.log('[ProductsDetailView] - onSubmit - create', data, updateLoad);

      try {
        await createTemplate(updateLoad, apiClient);
        navigate(ROUTE.FORMS);
      } catch (error) {
        console.log('[ProductDetailView] - error:', error);
      }
    } else {
      const updateLoad: TemplatesTypes.TemplateUpdateInput = {
        templateId: data?.templateId,
        name: data?.name,
        description: data?.description,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        steps: JSON.stringify(formDetails?.steps ?? []) as any,
      };
      console.log('DEV: TO UPDATE', formDetails.steps);

      try {
        await updateTemplate(updateLoad, apiClient);
        navigate(ROUTE.FORMS);
        console.log('[ProductDetailView]  - successfully updated product with id ', data.templateId);
      } catch (error) {
        console.log('[ProductDetailView] - error:', error);
      }
    }
  };

  const onError: SubmitErrorHandler<Utils.DBItem<TemplatesTypes.SurveyTemplate>> = (errors) => {
    console.log('[ProductDetailView] - onError', errors);
  };

  return (
    <Box className="Products-root" sx={styles.root}>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Box className="Products-heading">
          {/* Widened to fill the header; the field itself keeps the shared FormInput look. */}
          <Box sx={{ flexGrow: 1, maxWidth: '420px', '.form-input, .MuiTextField-root': { width: '100%' } }}>
            <FormInput name="name" label={t('Name')} control={control} />
          </Box>
          {/* Same size and variants as the step editor's footer actions. */}
          <Box className="Buttons">
            <Button type="button" onClick={requestLeave} size="small" variant="outlined">
              {t('editor.action.cancel')}
            </Button>
            <Button type="submit" size="small" variant="contained" startIcon={<SaveTwoToneIcon />}>
              {t('editor.action.save')}
            </Button>
          </Box>
        </Box>

        <Box className="Form-form">
          <Box className="Form-column Form-editor">
            <SurveySingle />
          </Box>
          <Box className="Form-column Form-preview">{!!formDetails && <SurveyLive survey={formDetails} />}</Box>
        </Box>

        <ConfirmDialog
          open={confirmLeave}
          title={t('editor.leaveTemplate.title')}
          body={t('editor.leaveTemplate.body')}
          keepLabel={t('editor.leaveTemplate.keep')}
          confirmLabel={t('editor.leaveTemplate.confirm')}
          onCancel={() => setConfirmLeave(false)}
          onConfirm={leave}
        />
      </form>
    </Box>
  );
};

export default Template;
